/**
 * Unified ELD API Client
 *
 * Uses the unified ELD API at /v1/eld/*
 * All ELD providers use consistent endpoints with eldType name as path parameter.
 * The backend handles all eldType-specific logic.
 */
import { ClientApplication, ClientUser, ELDType } from '@numeo/types-core'
import { ELDPaginationInfo, ELDTrailer } from '@numeo/types-main-web-app'
import { ELDVehicle, FleetTruckLocation, FleetTrucksLocationsResponse, SyncVehiclesResponse } from '@numeo/types'
import axios from 'axios'

// ========================================
// Response Types
// ========================================

interface ELDPaginatedApiResponse<T> {
    success: boolean
    data?: T[]
    pagination?: ELDPaginationInfo
    message?: string
}

interface ConnectResponse {
    success: boolean
    application: ClientApplication
    user?: ClientUser
    message?: string
}

interface RequestIntegrationPayload {
    providerName: string
    websiteUrl?: string
    notes?: string
}

export interface AutoAssignDriversResult {
    newDriversCreated: number
    existingDriversAssigned: number
    totalAssignments: number
}

/** Per-truck billing outcome returned alongside a vehicle import (ADR-020). */
interface AddVehiclesBilling {
    /** Active subscriber over the block — the truck quantity was bumped (prorated). */
    charged?: boolean
    newTruckTotal?: number
}

interface AddVehiclesResult {
    success?: boolean
    application: ClientApplication
    message?: string
    /** IDs of the vehicles actually imported — excludes any dropped because their VIN/vehicleId
     *  already matched an existing truck (silently skipped server-side, not replaced). */
    importedVehicleIds?: string[]
    billing?: AddVehiclesBilling
}

export interface AutoAssignDriversResponse {
    success: boolean
    application: ClientApplication
    result: AutoAssignDriversResult
    message: string
}

interface AddTrailersResult {
    success: boolean
    data: { inserted: number; duplicates: number; invalid: number }
    message?: string
}

class EldApi {
    // ========================================
    // Core API Methods (generic for all providers)
    // ========================================

    /**
     * Get vehicles with pagination - fetches one page at a time
     */
    async getVehiclesPage<T>(eldType: ELDType, integrationId?: string, cursor?: string): Promise<{ data: T[]; pagination?: ELDPaginationInfo }> {
        const params: Record<string, string> = {}
        if (integrationId) params.integrationId = integrationId
        if (cursor) params.cursor = cursor
        const response = await axios.get<ELDPaginatedApiResponse<T>>(`/eld/${eldType}/vehicles`, { params })
        return { data: response.data.data || [], pagination: response.data.pagination }
    }

    /**
     * Get all vehicles by fetching all pages sequentially
     */
    async getVehicles<T>(eldType: ELDType, integrationId?: string): Promise<T[]> {
        const allData: T[] = []
        let cursor: string | undefined

        do {
            const result = await this.getVehiclesPage<T>(eldType, integrationId, cursor)
            allData.push(...result.data)
            cursor = result.pagination?.hasMore ? result.pagination.nextCursor : undefined
        } while (cursor)

        return allData
    }

    /**
     * Get trailers (non-powered assets) with pagination - fetches one page at a time.
     * Only supported by providers that expose a trailers/assets endpoint (e.g. Motive).
     */
    async getTrailersPage<T>(eldType: ELDType, integrationId?: string, cursor?: string): Promise<{ data: T[]; pagination?: ELDPaginationInfo }> {
        const params: Record<string, string> = {}
        if (integrationId) params.integrationId = integrationId
        if (cursor) params.cursor = cursor
        const response = await axios.get<ELDPaginatedApiResponse<T>>(`/eld/${eldType}/trailers`, { params })
        return { data: response.data.data || [], pagination: response.data.pagination }
    }

    /**
     * Get all trailers by fetching all pages sequentially
     */
    async getTrailers<T>(eldType: ELDType, integrationId?: string): Promise<T[]> {
        const allData: T[] = []
        let cursor: string | undefined

        do {
            const result = await this.getTrailersPage<T>(eldType, integrationId, cursor)
            allData.push(...result.data)
            cursor = result.pagination?.hasMore ? result.pagination.nextCursor : undefined
        } while (cursor)

        return allData
    }

    /**
     * Get drivers with pagination - fetches one page at a time
     */
    async getDriversPage<T>(eldType: ELDType, integrationId?: string, cursor?: string): Promise<{ data: T[]; pagination?: ELDPaginationInfo }> {
        const params: Record<string, string> = {}
        if (integrationId) params.integrationId = integrationId
        if (cursor) params.cursor = cursor
        const response = await axios.get<ELDPaginatedApiResponse<T>>(`/eld/${eldType}/drivers`, { params })
        return { data: response.data.data || [], pagination: response.data.pagination }
    }

    /**
     * Get all drivers by fetching all pages sequentially
     */
    async getDrivers<T>(eldType: ELDType, integrationId?: string): Promise<T[]> {
        const allData: T[] = []
        let cursor: string | undefined

        do {
            const result = await this.getDriversPage<T>(eldType, integrationId, cursor)
            allData.push(...result.data)
            cursor = result.pagination?.hasMore ? result.pagination.nextCursor : undefined
        } while (cursor)

        return allData
    }

    /**
     * Pull live trailer/asset locations from the provider and write them onto the matching trailers.
     * Only providers with a GPS-tracked asset gateway support this (currently Motive); anything else
     * answers 400. One batched call covers the whole fleet, so this is a single request, not one per
     * trailer.
     */
    async syncTrailerLocations(eldType: ELDType, integrationId: string): Promise<{ fetched: number; updated: number; linked: number }> {
        const response = await axios.post<{ data: { fetched: number; updated: number; linked: number } }>(`/eld/${eldType}/trailers/sync-locations`, {
            integrationId,
        })
        return response.data.data
    }

    /**
     * Connect an ELD eldType with credentials
     * Backend handles credential validation based on eldType type
     */
    async connect(eldType: ELDType, credentials: Record<string, string>): Promise<ConnectResponse> {
        const response = await axios.post<ConnectResponse>(`/eld/${eldType}/connect`, credentials)
        return response.data
    }

    /**
     * Request an integration for an ELD provider that is not yet supported
     */
    async requestIntegration(payload: RequestIntegrationPayload): Promise<{ success: boolean; message?: string }> {
        const response = await axios.post<{ success: boolean; message?: string }>('/eld/request-integration', payload)
        return response.data
    }

    /**
     * Disconnect an ELD eldType
     */
    async disconnect(eldType: ELDType, integrationId: string): Promise<ConnectResponse> {
        const response = await axios.delete<ConnectResponse>(`/eld/${eldType}/disconnect`, { params: { integrationId } })
        return response.data
    }

    /**
     * Add selected vehicles to trucks list.
     * `acceptOverage` acknowledges the per-truck overage cost so an active subscriber's import can
     * proceed past the included-truck block (ADR-020); the server then bumps the Stripe truck qty.
     */
    async addVehicles(eldType: ELDType, integrationId: string, vehicles: ELDVehicle[], acceptOverage?: boolean): Promise<AddVehiclesResult> {
        const response = await axios.post<AddVehiclesResult>(`/eld/${eldType}/vehicles/add`, {
            integrationId,
            vehicles,
            acceptOverage,
        })
        return response.data
    }

    /**
     * Auto-assign drivers from ELD to existing trucks
     */
    async autoAssignDrivers(eldType: ELDType, integrationId: string): Promise<AutoAssignDriversResponse> {
        const response = await axios.post<AutoAssignDriversResponse>(`/eld/${eldType}/drivers/auto-assign`, { integrationId })
        return response.data
    }

    /**
     * Add selected ELD trailers to the trailers collection (agentic onboarding fleet import).
     * Re-runs are safe — the server skips unit numbers that already exist for this company.
     */
    async addTrailers(eldType: ELDType, integrationId: string, trailers: ELDTrailer[]): Promise<AddTrailersResult> {
        const response = await axios.post<AddTrailersResult>(`/eld/${eldType}/trailers/add`, { integrationId, trailers })
        return response.data
    }

    /**
     * Synchronize vehicles from ELD with trucks list (add/update by integration).
     * Optional payload can be provided for conflict resolution (vinSelections).
     */
    async syncVehicles(eldType: ELDType, integrationId: string, payload?: { vinSelections?: Record<string, string> }): Promise<SyncVehiclesResponse> {
        const body: Record<string, unknown> = { integrationId }
        if (payload?.vinSelections) {
            body.vinSelections = payload.vinSelections
        }
        const response = await axios.post<SyncVehiclesResponse>(`/eld/${eldType}/vehicles/sync`, body)
        return response.data
    }

    // ========================================
    // Fleet-wide API (aggregated across all ELD integrations)
    // ========================================

    /**
     * Get one page of fleet trucks with their current locations.
     */
    private async getFleetTrucksLocationsPage(cursor?: string, forceRefresh?: boolean): Promise<FleetTrucksLocationsResponse> {
        const params: Record<string, string> = {}
        if (cursor) params.cursor = cursor
        if (forceRefresh) params.forceRefresh = 'true'
        const response = await axios.get<FleetTrucksLocationsResponse>('/eld/fleet/trucks/locations', { params })
        return response.data
    }

    /**
     * Get all fleet trucks with their current locations.
     * Fetches from all connected ELD integrations and returns
     * only trucks that are in the application's truck list. A Box Truck company's trucks without an ELD, placed by
     * their driver's phone (ADR-059), come back in the same list with `locationSource: 'phone'`, so every reader of
     * the fleet's positions treats them as it treats an ELD truck.
     */
    async getFleetTrucksLocations(options: { forceRefresh?: boolean } = {}): Promise<FleetTrucksLocationsResponse> {
        const allTrucks: FleetTrucksLocationsResponse['data']['trucks'] = []
        const phoneTrucks: FleetTruckLocation[] = []
        let cursor: string | undefined
        let lastResponse: FleetTrucksLocationsResponse | undefined
        let isFirstPage = true

        do {
            const response = await this.getFleetTrucksLocationsPage(cursor, isFirstPage ? options.forceRefresh : false)
            isFirstPage = false
            lastResponse = response
            if (response.success && response.data?.trucks) {
                allTrucks.push(...response.data.trucks)
            }
            if (response.success && response.data?.phoneTrucks) phoneTrucks.push(...response.data.phoneTrucks)
            cursor = response.data?.pagination?.hasMore ? response.data.pagination.nextCursor : undefined
        } while (cursor)

        if (!lastResponse) {
            return { success: false, data: { trucks: [], timestamp: new Date().toISOString(), summary: { total: 0, matched: 0, integrations: [] } } }
        }

        return {
            ...lastResponse,
            data: {
                ...lastResponse.data,
                trucks: [...allTrucks, ...phoneTrucks],
                // Already in `trucks`: a reader that took both lists would place a truck twice.
                phoneTrucks: undefined,
                pagination: { hasMore: false },
            },
        }
    }

    async dismissAutoSyncConflicts(integrationId: string): Promise<void> {
        await axios.delete(`/eld/${integrationId}/auto-sync/conflicts`)
    }
}

export const eldApi = new EldApi()
